import React, { useEffect, useState } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import FilterSection from './FilterSection';
import './CatalogFilter.css';

const TEXT = {
    filters: '\u0424\u0438\u043b\u044c\u0442\u0440\u044b',
    category: '\u041a\u0430\u0442\u0435\u0433\u043e\u0440\u0438\u044f',
    brand: '\u0411\u0440\u0435\u043d\u0434',
    condition: '\u0421\u043e\u0441\u0442\u043e\u044f\u043d\u0438\u0435',
    price: '\u0426\u0435\u043d\u0430',
    from: '\u041e\u0442',
    to: '\u0414\u043e',
    clear: '\u041e\u0447\u0438\u0441\u0442\u0438\u0442\u044c',
    show: '\u041f\u043e\u043a\u0430\u0437\u0430\u0442\u044c',
    sortPrice: '\u0421\u043e\u0440\u0442\u0438\u0440\u043e\u0432\u043a\u0430 \u043f\u043e \u0446\u0435\u043d\u0435',
    priceAsc: '\u041f\u043e \u0432\u043e\u0437\u0440\u0430\u0441\u0442\u0430\u043d\u0438\u044e',
    priceDesc: '\u041f\u043e \u0443\u0431\u044b\u0432\u0430\u043d\u0438\u044e',
};

const getCollection = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (Array.isArray(value?.$values)) {
        return value.$values;
    }

    return [];
};

const FilterOption = ({ option, checked, onChange }) => {
    return (
        <label className="catalog-filter-option">
            <input type="checkbox" checked={checked} onChange={(event) => onChange(option.Value, event.target.checked)} />
            <span className="catalog-filter-option__label">{option.Label}</span>
            <span className="catalog-filter-option__count">{option.Count}</span>
        </label>
    );
};

const FilterOptions = ({ options, selected, onChange }) => {
    if (!options.length) {
        return null;
    }

    return options.map((option) => (
        <FilterOption key={option.Value} option={option} checked={selected.has(String(option.Value))} onChange={onChange} />
    ));
};

export default function CatalogFilter({ filters, sortValue, onSortChange }) {
    const [open, setOpen] = useState(false);
    const categories = getCollection(filters?.Categories);
    const brands = getCollection(filters?.Brands);
    const conditions = getCollection(filters?.Conditions);
    const [priceMin, setPriceMin] = useState('');
    const [priceMax, setPriceMax] = useState('');
    const optionSelection = (options) => new Set([
        ...options.filter((option) => option.IsSelected).map((option) => String(option.Value)),
    ]);
    const [draftCategories, setDraftCategories] = useState(() => optionSelection(categories));
    const [draftBrands, setDraftBrands] = useState(() => optionSelection(brands));
    const [draftConditions, setDraftConditions] = useState(() => optionSelection(conditions));
    const [draftSort, setDraftSort] = useState(sortValue === 'price_asc' || sortValue === 'price_desc' ? sortValue : '');
    const hasFilters =
        draftCategories.size > 0 || draftBrands.size > 0 || draftConditions.size > 0 || Boolean(priceMin) || Boolean(priceMax) || Boolean(draftSort);
    const activeFilterCount = draftCategories.size + draftBrands.size + draftConditions.size + (priceMin !== '' ? 1 : 0) + (priceMax !== '' ? 1 : 0);

    useEffect(() => {
        document.body.style.overflow = open ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    const toggleDraft = (setter) => (value, checked) => {
        setter((current) => {
            const next = new Set(current);
            checked ? next.add(String(value)) : next.delete(String(value));
            return next;
        });
    };

    const handleApply = () => {
        onSortChange?.(draftSort);
        setOpen(false);
    };

    const handleClear = () => {
        setDraftCategories(new Set());
        setDraftBrands(new Set());
        setDraftConditions(new Set());
        setPriceMin('');
        setPriceMax('');
        setDraftSort('');
    };

    return (
        <>
            <button onClick={() => setOpen(true)} className="catalog-filter-button" type="button">
                <SlidersHorizontal size={16} />
                <span>{TEXT.filters}</span>
                {activeFilterCount > 0 && <span className="catalog-filter-button__count">{activeFilterCount}</span>}
            </button>

            {open && <div className="catalog-filter-overlay" onClick={() => setOpen(false)} />}

            <aside className={`catalog-filter ${open ? 'open' : ''}`}>
                <div className="catalog-filter__header">
                    <h2>{TEXT.filters}</h2>
                    <button onClick={() => setOpen(false)} className="catalog-filter__close" type="button">
                        <X size={18} />
                    </button>
                </div>

                <div className="catalog-filter__content">
                    <FilterSection title={TEXT.category} defaultOpen>
                        <FilterOptions options={categories} selected={draftCategories} onChange={toggleDraft(setDraftCategories)} />
                    </FilterSection>

                    <FilterSection title={TEXT.brand}>
                        <FilterOptions options={brands} selected={draftBrands} onChange={toggleDraft(setDraftBrands)} />
                    </FilterSection>

                    <FilterSection title={TEXT.condition}>
                        <FilterOptions options={conditions} selected={draftConditions} onChange={toggleDraft(setDraftConditions)} />
                    </FilterSection>

                    <FilterSection title={TEXT.price}>
                        <div className="catalog-filter-price">
                            <input
                                type="number"
                                inputMode="numeric"
                                placeholder={`${TEXT.from} ${filters?.Price?.Min ?? ''}`.trim()}
                                value={priceMin}
                                onChange={(event) => setPriceMin(event.target.value)}
                            />
                            <input
                                type="number"
                                inputMode="numeric"
                                placeholder={`${TEXT.to} ${filters?.Price?.Max ?? ''}`.trim()}
                                value={priceMax}
                                onChange={(event) => setPriceMax(event.target.value)}
                            />
                        </div>
                    </FilterSection>

                    <FilterSection title={TEXT.sortPrice} defaultOpen>
                        <div className="catalog-filter-sort">
                            <label className="catalog-filter-sort__option">
                                <input type="radio" name="catalog-sort" value="price_asc" checked={draftSort === 'price_asc'} onChange={(event) => setDraftSort(event.target.value)} />
                                <span>{TEXT.priceAsc}</span>
                            </label>
                            <label className="catalog-filter-sort__option">
                                <input type="radio" name="catalog-sort" value="price_desc" checked={draftSort === 'price_desc'} onChange={(event) => setDraftSort(event.target.value)} />
                                <span>{TEXT.priceDesc}</span>
                            </label>
                        </div>
                    </FilterSection>
                </div>

                <div className="catalog-filter__bottom">
                    {hasFilters && (
                        <button onClick={handleClear} className="catalog-filter__clear" type="button">
                            {TEXT.clear}
                        </button>
                    )}

                    <button className="catalog-filter__apply" type="button" onClick={handleApply}>
                        {TEXT.show}
                    </button>
                </div>
            </aside>
        </>
    );
}
